import React, { useContext, useState } from 'react';
import { Trans, useI18nContext } from '@ecg-internal/js-react-i18n';
import { StandAloneLink, TextRegular } from '@hz-design-system/web-ui';

import FavouriteButton from '../../../ListingApp/components/Listing/FavouriteButton';
import EnvironmentContext from '../../../../contexts/EnvironmentContext';

import Classes from './ListingHeader.module.scss';
import { TListingClient } from 'types/TListing';
import getAdUrl from '../../../SocialShareApp/utils/getAdUrl';
import isMobile from '../../../../utils/isMobile';
import formatPrice from '../../../../utils/formatPrice';
import ShippingInformation from '../../../ListingApp/components/Listing/ShippingInformation';
import BuyersProtectionModalLink from '../../../ListingApp/components/Listing/BuyersProtection/ModalLink/ModalLink';
import { trackGAEvent } from '../../../../utils/gaTracking';
import { BusinessSellerBuyNowInfo } from './BusinessSellerBuyNowInfo';
import { SELLER_TYPE, priceTypes } from '../../../../../../common/constants';

type TListingHeaderProps = {
  listing: TListingClient;
  financeAvailableHighlight?: string;
};

let SocialShare;
let BasicModal;

const ListingHeader = ({ listing, financeAvailableHighlight }: TListingHeaderProps) => {
  const { t } = useI18nContext();
  const { userDetails: { isAdOwner = false } = {}, buyerProtectionEnabled } = useContext(EnvironmentContext);
  const {
    itemId,
    title,
    priceInfo,
    flags: { isAdmarkt, isLeaseCar, showLoanIndicators, shippable },
    isSavingEnabled,
    isSaved,
    isCarAd,
    shippingInformation,
    buyersProtectionAllowed,
    stats,
  } = listing;
  const isCurrency = [priceTypes.MIN_BID, priceTypes.FIXED].includes(priceInfo.priceType);
  const isPricePerMonth = isLeaseCar && isCurrency && !listing.isReserved;
  const [isSocialShareModalVisible, setIsSocialShareModalVisible] = useState(false);

  const isBusinessSellerBuyNow = listing.buyItNowEnabled && listing.seller.sellerType === SELLER_TYPE.TRADER;

  const handleShareToggle = async (e) => {
    e.preventDefault();
    const shareData = { title, url: getAdUrl() };
    const navigatorShareIsAvailable = navigator.canShare && navigator.canShare(shareData) && isMobile();

    if (navigatorShareIsAvailable) {
      trackGAEvent('ShareAdBegin', 'navigator');
      navigator.share(shareData);
    } else {
      SocialShare = (
        await import(/* webpackChunkName: "socialShare" */ '../../../SocialShareApp/components/SocialShare/SocialShare')
      ).default;
      BasicModal = (await import(/* webpackChunkName: "BasicModal" */ '@hz-design-system/web-ui')).BasicModal;
      setIsSocialShareModalVisible(true);
    }
  };

  return (
    <>
      <div className={Classes.actionsContainer}>
        <div className={Classes.actions}>
          {isSavingEnabled && !isAdOwner && (
            <div className={Classes.action}>
              <FavouriteButton
                asLink
                isSaved={isSaved}
                itemId={itemId}
                favoritedCount={stats.favoritedCount}
                isDisplayedOnMobile
              />
            </div>
          )}
          <div className={Classes.action}>
            <StandAloneLink onClick={handleShareToggle} aria-label={t('share.with')} iconLeft="share_web">
              <Trans tagName="span" i18nKey={'share.label'} />
            </StandAloneLink>
          </div>
        </div>
      </div>

      <header className={`${Classes.header} hz-Page-section hz-Page-section--separated`}>
        <h1 className={Classes.title}>{title}</h1>
        {isPricePerMonth && (
          <>
            <div className={Classes.price}>
              <span className={Classes.pricePerMonth}>{t('listing.leaseprice.prefix')}</span>
              <span>{` ${formatPrice(listing.priceInfo, listing.isReserved, t, isCarAd)} `}</span>
              <span className={Classes.pricePerMonth}>{` ${t('listing.leaseprice.suffix')}`}</span>
              {showLoanIndicators && <span>*</span>}
            </div>
            <TextRegular color="text-secondary" utilities={['marginTopXxs']}>
              {t('listing.leaseprice.subText')}
            </TextRegular>
          </>
        )}
        {!isPricePerMonth && (
          <>
            <div className={Classes.price}>{formatPrice(listing.priceInfo, listing.isReserved, t, isCarAd)}</div>
            {isCarAd && financeAvailableHighlight && (
              <TextRegular color="text-secondary" utilities={['marginTopXxs']}>
                {financeAvailableHighlight}
              </TextRegular>
            )}
          </>
        )}
        {!isCarAd && (
          <ShippingInformation shippingInformation={shippingInformation} shippable={shippable} isAdmarkt={isAdmarkt} />
        )}
        {buyersProtectionAllowed && buyerProtectionEnabled && !isCarAd && !isBusinessSellerBuyNow && (
          <BuyersProtectionModalLink />
        )}
        {isBusinessSellerBuyNow && <BusinessSellerBuyNowInfo sellerId={listing.seller.id} />}
      </header>
      {isSocialShareModalVisible && (
        <BasicModal size="m" title={t('share.label')} onClose={() => setIsSocialShareModalVisible(false)}>
          <SocialShare listing={listing} asModal />
        </BasicModal>
      )}
    </>
  );
};

export default ListingHeader;
